import React, { useEffect, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { useState } from "react";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  editPromotionMailTemplate,
  listPromotionMailTemplates,
} from "./PromotionMailTemplateActions";
import {
  PromotionMailTemplate,
  PromotionMailTemplateUpdateForm,
  MailTemplateContentType,
} from "api/sms-api";
import { Row, Col, RadioChangeEvent, Radio, Spin } from "antd";
import { Formik } from "formik";
import { Form, Input } from "formik-antd";
import { RootState } from "store/store";
import i18n from "i18n";
import { promotionMailTemplateTagItems } from "../../constants";
import SaveButton from "components/SaveButton";
import { promotionMailTemplateFormSchema } from "validation";
import RichEditor from "components/RichEditor";
import OptoutPageTemplatePreviewDialog from "features/optout_page_template/OptoutPageTemplatePreviewDialog";

interface PromotionMailTemplatesProps {
  items: Array<PromotionMailTemplate>;
  loading: boolean;
  listPromotionMailTemplates: () => void;
  setPage: (page: Page) => void;

  editPromotionMailTemplate: (
    id: string,
    params: PromotionMailTemplateUpdateForm
  ) => void;
  editLoading: boolean;
  editFinished: boolean;
}

const PromotionMailTemplates: React.FC<
  PromotionMailTemplatesProps & RouteComponentProps
> = (props) => {
  const [
    promotionMailTemplate,
    setPromotionMailTemplate,
  ] = useState<PromotionMailTemplate>(
    props.items.length > 0 ? props.items[0] : ({} as PromotionMailTemplate)
  );
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const formikRef = useRef<any>(null);

  useEffect(() => {
    props.setPage(Page.promotionMailTemplates);
  }, [props.setPage]);

  useEffect(() => {
    props.listPromotionMailTemplates();
  }, [props.listPromotionMailTemplates]);

  useEffect(() => {
    if (props.items.length > 0 && !promotionMailTemplate.id) {
      setPromotionMailTemplate(props.items[0]);
    }
  }, [props.items, promotionMailTemplate.id]);

  if (props.loading) {
    return <Spin />;
  }

  const initialValues = {
    fortuneSettingsTypeId: promotionMailTemplate.fortuneSettingsTypeId || "",
    title: promotionMailTemplate.title || "",
    contentType:
      promotionMailTemplate.contentType || MailTemplateContentType.Text,
    contents: promotionMailTemplate.contents || "",
  };

  const previewButton: any = {
    name: i18n.t("mailTemplate.optoutPageTemplate.preview"),
    icon: "eye",
    exec: function () {
      setIsPreviewModalOpen(true);
    },
  };

  const handleSubmit = (values: PromotionMailTemplateUpdateForm) => {
    const id = promotionMailTemplate?.id || "";
    props.editPromotionMailTemplate(id, values);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={promotionMailTemplateFormSchema}
    >
      {(formikBag) => {
        // Store formikBag in ref for use in effects
        formikRef.current = formikBag;

        const handleFortuneSettingsTypeChange = (e: RadioChangeEvent) => {
          const value = e.target.value;
          const item =
            props.items.find((item) => item.fortuneSettingsTypeId === value) ||
            ({} as PromotionMailTemplate);
          setPromotionMailTemplate(item);
          // Update Formik values immediately
          formikBag.setFieldValue(
            "fortuneSettingsTypeId",
            item.fortuneSettingsTypeId || ""
          );
          formikBag.setFieldValue("title", item.title || "");
          formikBag.setFieldValue(
            "contentType",
            item.contentType || MailTemplateContentType.Text
          );
          formikBag.setFieldValue("contents", item.contents || "");
        };

        return (
          <Form layout="vertical">
            <div className="question-container">
              <Row>
                <Col span={18}>
                  <Form.Item name="fortuneSettingsTypeId">
                    <Radio.Group
                      value={promotionMailTemplate.fortuneSettingsTypeId}
                      onChange={handleFortuneSettingsTypeChange}
                    >
                      {props.items.map((type: PromotionMailTemplate) => (
                        <Radio
                          key={type.fortuneSettingsTypeId}
                          value={type.fortuneSettingsTypeId}
                        >
                          {type.fortuneSettingsTypeName}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={18}>
                  <Form.Item
                    name="title"
                    label={i18n.t("mailTemplate.table.title.title")}
                  >
                    <Input name="title" />
                  </Form.Item>
                  <Form.Item
                    name="contentType"
                    label={i18n.t("mailTemplate.table.title.contentType")}
                  >
                    <Radio.Group
                      name="contentType"
                      className="form-radio-group"
                      defaultValue={initialValues.contentType}
                    >
                      <Radio.Button value={MailTemplateContentType.Text}>
                        {i18n.t("mailTemplateContentTypes.text")}
                      </Radio.Button>
                      <Radio.Button value={MailTemplateContentType.Html}>
                        {i18n.t("mailTemplateContentTypes.html")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name="contents"
                    label={i18n.t("mailTemplate.table.title.contents")}
                  >
                    <RichEditor
                      hideButtonList={["preview"]}
                      customButtons={[previewButton]}
                      tagList={promotionMailTemplateTagItems}
                      value={formikBag.values.contents || ""}
                      // Force RichEditor to re-render when switching between different fortune settings types
                      // to prevent stale content from being displayed.
                      recomputeValue={formikBag.values.fortuneSettingsTypeId}
                      onChange={(value) => {
                        formikBag.setFieldValue("contents", value);
                      }}
                    />
                  </Form.Item>
                  <OptoutPageTemplatePreviewDialog
                    content={formikBag.values.contents || ""}
                    visible={isPreviewModalOpen}
                    closeModal={closePreviewModal}
                  />
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: "2.5em" }}>
                <SaveButton />
              </Row>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    items: state.promotionMailTemplates.items,
    loading: state.promotionMailTemplates.listLoading,
    editLoading: state.promotionMailTemplates.editLoading,
    editFinished: state.promotionMailTemplates.editFinished,
  };
};

const mapDispatchToProps = {
  setPage,
  listPromotionMailTemplates,
  editPromotionMailTemplate,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PromotionMailTemplates);
